import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import axiosInstance, { checkTokenValidity, getUserDetails,
  handleLegacySessionExpired
} from '../../components/content/axiosConfig';
import { fetchPondsForCurrentUser } from '../../utils/pondListForUser';
import Preloader from '../../components/custom/preLoader';
import showErrorToast, { showSuccessToast } from '../../components/toast/toast';
import PondDetailStandardWeightUnits from '../../components/custom/PondDetailStandardWeightUnits';
import FishTypesSelection from '../../components/custom/FishTypesSelection';
import SamplingEffortFields, {
  buildDefaultAnglerSlots,
} from '../../components/custom/SamplingEffortFields';
import ClockTimeInput from '../../components/custom/ClockTimeInput';
import { requiresEffortUnits, isAnglingSamplingMethod, SAMPLING_METHOD_OPTIONS, isValidClockTimeInput } from '../../utils/samplingCpueUtils';
import { displayMethodLabel } from '../../utils/samplingDisplayLabels';

const WQ_EXTRA_FIELD_DEFS = [
  { id: 'BOD5', label: 'BOD5, mg/L' },
  { id: 'COD', label: 'COD, mg/L' },
  { id: 'Alkalinity', label: 'Alkalinity, mg/L CaCO3' },
  { id: 'NitriteN', label: 'Nitrite-N, mg/L NO2-N' },
  { id: 'TotalHardness', label: 'Total Hardness, mg/L CaCO3' },
  { id: 'PhosphateIon', label: 'Phosphate Ion, mg/L PO4' },
  { id: 'Phosphorous', label: 'Phosphorus, mg/L P' },
  { id: 'TotalAmmoniaNitrogen', label: 'Total Ammonia Nitrogen, mg/L NH3-N' },
];

const optionalWQFloat = (v) => {
  if (v === '' || v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const CreatePondDetail = () => {
  const history = useHistory();
  const { id } = useParams();
  const sessionFisherID = getUserDetails()?.FisherID;
  const [ponds, setPonds] = useState([]);
  const [fishTypes, setFishTypes] = useState([]);
  const [selectedFishTypes, setSelectedFishTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formErrors, setFormErrors] = useState({});
  const [selectedWeightUnit, setSelectedWeightUnit] = useState('');
  const [selectedLengthUnit, setSelectedLengthUnit] = useState('');

  // Helper functions for date formatting
  const formatDate = (dateObj) => {
    if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj)) return '';
    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const formatLocalDateTime = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date)) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Initial form state
  const initialFormState = {
    PondID: id || '0',
    Name: formatDate(new Date()),
    Date: new Date(),
    TimeFishingStarted: '',
    TimeFishingStopped: '',
    SamplingMethod: '',
    AirTemperature: '',
    SkyClimate: '',
    WindSpeed: '',
    WindDirection: '',
    MoonPhase: '',
    BaroMetricPressure: '',
    WaterTemperature: '',
    WaterClarity: '',
    PH: '',
    DO: '',
    BOD5: '',
    COD: '',
    Alkalinity: '',
    NitriteN: '',
    TotalHardness: '',
    PhosphateIon: '',
    Phosphorous: '',
    TotalAmmoniaNitrogen: '',
    NoFishes: 0,
    NoFishTypes: 0,
    FisherID: sessionFisherID ?? '',
    Weight_Unit: '',
    Length_Unit: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [effortCount, setEffortCount] = useState('');
  const [effortUnits, setEffortUnits] = useState([]);
  const [anglerCount, setAnglerCount] = useState("");
  const [fisherStaffOptions, setFisherStaffOptions] = useState([]);
  const [sessionAnglerSlots, setSessionAnglerSlots] = useState([]);

  // Fetch fish types
  const fetchFishTypes = useCallback(async (pondId) => {
    if (!pondId || pondId === '0') return;

    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/api/PondFishes/GetAllPondFishes/${pondId}`, {
        params: { search: '' },
      });
      setFishTypes(response.data || []);
      setSelectedFishTypes([]);
    } catch (error) {
      handleApiError(error, 'Failed to fetch fish types');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch ponds data
  const fetchPonds = useCallback(async () => {
    try {
      setIsLoading(true);
      const fetchedPonds = await fetchPondsForCurrentUser(getUserDetails());
      setPonds(fetchedPonds);

      if (id) {
        const selectedPond = fetchedPonds.find((pond) => pond.id === Number(id));
        if (selectedPond) {
          setFormData((prev) => ({
            ...prev,
            PondID: id,
            FisherID: selectedPond.FisherID ?? sessionFisherID ?? prev.FisherID,
            Name: `${selectedPond.Name}-${formatDate(new Date())}`,
          }));
          fetchFishTypes(id);
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to fetch ponds');
    } finally {
      setIsLoading(false);
    }
  }, [sessionFisherID, id, fetchFishTypes]);

  // Handle API errors
  const handleApiError = (error, defaultMessage) => {
    if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
      const errorMessage = error.response?.data?.message || defaultMessage;
      showErrorToast(errorMessage);
      console.error('API Error:', error);
    }
  };

  // Handle pond selection change
  const handlePondChange = (e) => {
    const { value } = e.target;
    const selectedPond = ponds.find((item) => item.id === Number(value));

    setFormErrors((prev) => ({ ...prev, PondID: '' }));
    setFormData((prev) => ({
      ...prev,
      PondID: value,
      FisherID: selectedPond?.FisherID ?? sessionFisherID ?? prev.FisherID,
      Name: selectedPond ? `${selectedPond.Name}-${formatDate(prev.Date || new Date())}` : formatDate(prev.Date || new Date()),
    }));

    fetchFishTypes(value);
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormErrors((prev) => ({ ...prev, [id]: '' }));
    if (id === 'SamplingMethod') {
      setEffortCount('');
      setEffortUnits([]);
      if (!isAnglingSamplingMethod(value)) {
        setAnglerCount('');
        setSessionAnglerSlots([]);
      }
    }
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const fetchFisherTeamStaff = useCallback(async (scopedFisherID) => {
    const fid = scopedFisherID ?? formData.FisherID ?? sessionFisherID;
    if (!fid) {
      setFisherStaffOptions([]);
      return;
    }
    try {
      const res = await axiosInstance.get('/api/PondDetail/FisherTeamStaff', {
        params: { FisherID: fid },
      });
      const options = (res.data?.staff || []).map((s) => ({
        value: s.id,
        label: s.fullName,
      }));
      setFisherStaffOptions(options);
    } catch {
      setFisherStaffOptions([]);
    }
  }, [formData.FisherID, sessionFisherID]);

  const handleAnglerCountChange = (count) => {
    setAnglerCount(count);
    setFormErrors((prev) => ({ ...prev, SessionAnglers: '' }));
  };

  const handleSessionAnglerSlotsChange = (slots) => {
    setSessionAnglerSlots(slots || []);
    setFormErrors((prev) => ({ ...prev, SessionAnglers: '' }));
  };

  const saveSessionAnglers = async (pondDetailId, slots) => {
    if (!slots?.length) return;
    await axiosInstance.put(`/api/SamplingSessionAnglers/${pondDetailId}`, {
      anglers: slots.map((slot, i) => ({
        id: slot.id || undefined,
        staffId: slot.staffId || null,
        label: slot.label || `Angler ${i + 1}`,
        sortOrder: slot.sortOrder ?? i,
      })),
    });
  };

  const saveEffortUnits = async (pondDetailId, samplingMethod, units) => {
    if (!requiresEffortUnits(samplingMethod) || !units?.length) return;
    await axiosInstance.put(`/api/SamplingEffort/${pondDetailId}`, {
      samplingMethod,
      units,
    });
  };

  // Handle date changes — ignore empty/invalid picker events (they used to snap to now).
  const handleDateChange = (e) => {
    const newDateTime = e.target.value;
    if (!newDateTime) return;
    const newDate = new Date(newDateTime);
    if (Number.isNaN(newDate.getTime())) return;

    const formattedDate = formatDate(newDate);
    const selectedPond = ponds.find((item) => item.id === Number(formData.PondID));

    setFormData((prev) => ({
      ...prev,
      Date: newDate,
      Name: selectedPond ? `${selectedPond.Name}-${formattedDate}` : formattedDate,
    }));
  };

  const handleStandardWeightUnitsChange = ({ weightUnitId, lengthUnitId }) => {
    setSelectedWeightUnit(weightUnitId || '');
    setSelectedLengthUnit(lengthUnitId || '');
    setFormData((prev) => ({
      ...prev,
      Weight_Unit: weightUnitId || '',
      Length_Unit: lengthUnitId || '',
    }));
    setFormErrors((prev) => ({
      ...prev,
      Weight_Unit: '',
      Length_Unit: '',
      StandardWeightSystem: '',
    }));
  };

  // Handle fish types change
  const handleFishTypesChange = (selectedOptions) => {
    setSelectedFishTypes(selectedOptions || []);
    setFormErrors((prev) => ({ ...prev, FishTypes: '' }));
    setFormData((prev) => ({
      ...prev,
      NoFishTypes: selectedOptions ? selectedOptions.length : 0,
    }));
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    const requiredFields = [
      { field: 'PondID', message: 'Please select a pond', check: (val) => val && val !== '0' },
      { field: 'Name', message: 'Name is required', check: (val) => val?.trim() },
      { field: 'TimeFishingStarted', message: 'Timer start is required (h:mm AM/PM)', check: (val) => val?.trim() && isValidClockTimeInput(val) },
      { field: 'TimeFishingStopped', message: 'Timer stop is required (h:mm AM/PM)', check: (val) => val?.trim() && isValidClockTimeInput(val) },
      { field: 'SamplingMethod', message: 'Sampling method is required', check: (val) => val?.trim() },
      {
        field: 'StandardWeightSystem',
        message: 'Standard weight system is required',
        check: () => formData.Weight_Unit && formData.Length_Unit,
      },
      { field: 'FishTypes', message: 'At least one fish type is required', check: () => selectedFishTypes.length > 0 },
    ];

    requiredFields.forEach(({ field, message, check }) => {
      if (!check(formData[field])) {
        errors[field] = message;
        isValid = false;
      }
    });

    if (isAnglingSamplingMethod(formData.SamplingMethod)) {
      const n = parseInt(anglerCount, 10);
      if (!Number.isFinite(n) || n < 1 || sessionAnglerSlots.length < 1) {
        errors.SessionAnglers = 'Enter the number of anglers for this fishing session';
        isValid = false;
      } else if (sessionAnglerSlots.some((slot) => !(slot.label || '').trim())) {
        errors.SessionAnglers = 'Each angler slot needs a label';
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        ...formData,
        FisherID: formData.FisherID || sessionFisherID,
        Date: formData.Date?.toISOString() || new Date().toISOString(),
        NumberOfAnglers: isAnglingSamplingMethod(formData.SamplingMethod)
          ? parseInt(anglerCount, 10)
          : null,
        FertilizationDate: null,
        FertilizationType: '',
        BOD5: optionalWQFloat(formData.BOD5),
        COD: optionalWQFloat(formData.COD),
        Alkalinity: optionalWQFloat(formData.Alkalinity),
        NitriteN: optionalWQFloat(formData.NitriteN),
        TotalHardness: optionalWQFloat(formData.TotalHardness),
        PhosphateIon: optionalWQFloat(formData.PhosphateIon),
        Phosphorous: optionalWQFloat(formData.Phosphorous),
        TotalAmmoniaNitrogen: optionalWQFloat(formData.TotalAmmoniaNitrogen),
      };

      const response = await axiosInstance.post('/api/PondDetail/AddPondDetail', payload);
      const pondDetailId = response.data.pondDetail.id;

      await saveEffortUnits(pondDetailId, formData.SamplingMethod, effortUnits);
      if (isAnglingSamplingMethod(formData.SamplingMethod)) {
        await saveSessionAnglers(pondDetailId, sessionAnglerSlots);
      }

      let existingFishTypes = [];
      try {
        const fishResponse = await axiosInstance.get(`/api/SamplingFishes/GetAllFishesBYPondDID/${pondDetailId}`);
        existingFishTypes = fishResponse.data || [];
      } catch (error) {
        console.warn('No existing fish types found or error fetching:', error);
      }

      const selectedFishTypeIds = selectedFishTypes.map((option) => option.value);
      const existingFishTypeIds = existingFishTypes.map((fish) => fish.FishTypeID);

      const fishTypesToAdd = selectedFishTypeIds.filter((id) => !existingFishTypeIds.includes(id));
      const fishTypesToDelete = existingFishTypes.filter((fish) => !selectedFishTypeIds.includes(fish.FishTypeID));

      for (const fishTypeId of fishTypesToAdd) {
        await axiosInstance.post(`/api/SamplingFishes/AddFish`, {
          PondDID: pondDetailId,
          FishTypeID: fishTypeId,
          FisherID: formData.FisherID || sessionFisherID,
          NumberOfFishes: 0,
          AverageLength: 0,
          StandardDeviationLength: 0,
          AverageWeight: 0,
          StandardDeviationWeight: 0,
          AverageWr: 0,
          StandardDeviationWr: 0,
          NumberOfFishesReleased: 0,
          NumberOfFishesCulled: 0,
        });
      }

      for (const fish of fishTypesToDelete) {
        await axiosInstance.delete(`/api/SamplingFishes/DeleteFish/${fish.PondFishesID}`);
      }

      showSuccessToast('Pond detail and fish types saved successfully');
      history.push(`/Updatepd/${pondDetailId}`);
    } catch (error) {
      handleApiError(error, 'Failed to save pond detail or fish types');
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize component
  useEffect(() => {
    checkTokenValidity();
    fetchPonds();
  }, [fetchPonds]);

  useEffect(() => {
    if (isAnglingSamplingMethod(formData.SamplingMethod)) {
      fetchFisherTeamStaff(formData.FisherID);
    } else {
      setFisherStaffOptions([]);
      setSessionAnglerSlots([]);
      setAnglerCount('');
    }
  }, [formData.SamplingMethod, formData.FisherID, fetchFisherTeamStaff]);

  useEffect(() => {
    if (!isAnglingSamplingMethod(formData.SamplingMethod)) return;
    if (sessionAnglerSlots.length > 0 || (anglerCount && parseInt(anglerCount, 10) > 0)) return;
    const me = fisherStaffOptions.find(
      (opt) => String(opt.value) === String(getUserDetails()?.id)
    );
    setAnglerCount('1');
    setSessionAnglerSlots(
      buildDefaultAnglerSlots(1, [
        {
          id: null,
          label: 'Angler 1',
          staffId: me?.value ?? null,
          fullName: me?.label ?? null,
          sortOrder: 0,
        },
      ])
    );
  }, [formData.SamplingMethod, fisherStaffOptions, sessionAnglerSlots.length, anglerCount]);

  if (isLoading) {
    return <Preloader />;
  }

  return (
    <div className="donezo-page donezo-list-page donezo-orders-page donezo-workflow-page donezo-upload-page">
      <form onSubmit={handleSubmit}>
        <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
          <div className="donezo-orders-toolbar donezo-upload-toolbar">
            <div className="donezo-upload-toolbar__intro">
              <ul className="breadcrumb mb-2">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/fishingData">Fishing Data</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Create Fishing / Sampling Data
                </li>
              </ul>
              <h1 className="donezo-orders-toolbar__title">
                Create Fishing / Sampling Data
                {formData.Name ? ` (${formData.Name})` : ""}
              </h1>
              <p className="donezo-upload-toolbar__subtitle">
                Enter session details, sampling effort, units, and water quality
              </p>
            </div>
            <div className="donezo-orders-toolbar__actions">
              <button
                type="submit"
                className="donezo-orders-toolbar__primary"
                disabled={isLoading}
              >
                {isLoading ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>

          <div className="donezo-orders-panel donezo-upload-section mb-3">
            <div className="donezo-upload-section__header">
              <i className="fa fa-info-circle" aria-hidden />
              <h2 className="donezo-upload-section__title">Basic Information</h2>
            </div>
            <div className="donezo-upload-section__body">
              <div className="row g-3">
                <div className="col-12 col-lg-4">
                  <div className="form-floating">
                    <select
                      className={`form-control ${formErrors.PondID ? "is-invalid" : ""}`}
                      id="PondID"
                      name="PondID"
                      disabled={!!id}
                      value={formData.PondID}
                      onChange={handlePondChange}
                    >
                      <option value="0">Select Pond</option>
                      {ponds.map((pond) => (
                        <option key={pond.id} value={pond.id}>
                          {pond.Name}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="PondID">
                      Pond <span className="text-danger">*</span>
                    </label>
                    {formErrors.PondID && (
                      <div className="invalid-feedback">{formErrors.PondID}</div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-lg-4">
                  <div className="form-floating">
                    <input
                      type="datetime-local"
                      className={`form-control ${formErrors.Date ? "is-invalid" : ""}`}
                      id="Date"
                      value={formData.Date ? formatLocalDateTime(formData.Date) : ""}
                      onChange={handleDateChange}
                    />
                    <label htmlFor="Date">
                      Sampling/Fishing Date &amp; Time{" "}
                      <span className="text-danger">*</span>
                    </label>
                    {formErrors.Date && (
                      <div className="invalid-feedback">{formErrors.Date}</div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-lg-4">
                  <FishTypesSelection
                    fishTypes={fishTypes}
                    selectedFishTypes={selectedFishTypes}
                    onChange={handleFishTypesChange}
                    error={formErrors.FishTypes}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="donezo-orders-panel donezo-upload-section mb-3">
            <div className="donezo-upload-section__header">
              <i className="fa fa-clock" aria-hidden />
              <h2 className="donezo-upload-section__title">Sampling Details</h2>
            </div>
            <div className="donezo-upload-section__body">
              <div className="row g-3">
                <div className="col-12 col-md-6 col-lg-3">
                  <div className="form-floating">
                    <ClockTimeInput
                      className={`form-control ${
                        formErrors.TimeFishingStarted ? "is-invalid" : ""
                      }`}
                      id="TimeFishingStarted"
                      value={formData.TimeFishingStarted}
                      onChange={handleChange}
                      placeholder="1200 AM"
                    />
                    <label htmlFor="TimeFishingStarted">
                      Timer start (h:mm AM/PM){" "}
                      <span className="text-danger">*</span>
                    </label>
                    {formErrors.TimeFishingStarted && (
                      <div className="invalid-feedback">
                        {formErrors.TimeFishingStarted}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-6 col-lg-3">
                  <div className="form-floating">
                    <ClockTimeInput
                      className={`form-control ${
                        formErrors.TimeFishingStopped ? "is-invalid" : ""
                      }`}
                      id="TimeFishingStopped"
                      value={formData.TimeFishingStopped}
                      onChange={handleChange}
                      placeholder="1214 PM"
                    />
                    <label htmlFor="TimeFishingStopped">
                      Timer stop (h:mm AM/PM){" "}
                      <span className="text-danger">*</span>
                    </label>
                    {formErrors.TimeFishingStopped && (
                      <div className="invalid-feedback">
                        {formErrors.TimeFishingStopped}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-6 col-lg-3">
                  <div className="form-floating">
                    <select
                      className={`form-control ${
                        formErrors.SamplingMethod ? "is-invalid" : ""
                      }`}
                      id="SamplingMethod"
                      value={formData.SamplingMethod}
                      onChange={handleChange}
                    >
                      <option value="">Select Sampling Method</option>
                      {SAMPLING_METHOD_OPTIONS.map((m) => (
                        <option key={m} value={m}>
                          {displayMethodLabel(m)}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="SamplingMethod">
                      Sampling Method <span className="text-danger">*</span>
                    </label>
                    {formErrors.SamplingMethod && (
                      <div className="invalid-feedback">
                        {formErrors.SamplingMethod}
                      </div>
                    )}
                  </div>
                </div>

                <SamplingEffortFields
                  samplingMethod={formData.SamplingMethod}
                  effortCount={effortCount}
                  onEffortCountChange={setEffortCount}
                  effortUnits={effortUnits}
                  onEffortUnitsChange={setEffortUnits}
                  anglerCount={anglerCount}
                  onAnglerCountChange={handleAnglerCountChange}
                  fisherStaffOptions={fisherStaffOptions}
                  sessionAnglerSlots={sessionAnglerSlots}
                  onSessionAnglerSlotsChange={handleSessionAnglerSlotsChange}
                  sessionAnglersError={formErrors.SessionAnglers}
                />
              </div>
            </div>
          </div>

          <div className="donezo-orders-panel donezo-upload-section mb-3">
            <div className="donezo-upload-section__header">
              <i className="fa fa-cloud-sun" aria-hidden />
              <h2 className="donezo-upload-section__title">Weather Conditions</h2>
            </div>
            <div className="donezo-upload-section__body">
              <div className="row g-3">
                <div className="col-12 col-md-6 col-lg-4">
                  <div className="form-floating">
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control ${
                        formErrors.AirTemperature ? "is-invalid" : ""
                      }`}
                      id="AirTemperature"
                      value={formData.AirTemperature}
                      onChange={handleChange}
                    />
                    <label htmlFor="AirTemperature">Air Temperature (°F)</label>
                    {formErrors.AirTemperature && (
                      <div className="invalid-feedback">
                        {formErrors.AirTemperature}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <div className="form-floating">
                    <select
                      className={`form-control ${
                        formErrors.SkyClimate ? "is-invalid" : ""
                      }`}
                      id="SkyClimate"
                      value={formData.SkyClimate}
                      onChange={handleChange}
                    >
                      <option value="">Select Sky</option>
                      <option value="Sunny">Sunny</option>
                      <option value="Cloudy">Cloudy</option>
                      <option value="Partly Cloudy">Partly Cloudy</option>
                      <option value="Rain">Rain</option>
                      <option value="Snow">Snow</option>
                    </select>
                    <label htmlFor="SkyClimate">Sky</label>
                    {formErrors.SkyClimate && (
                      <div className="invalid-feedback">{formErrors.SkyClimate}</div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <div className="form-floating">
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control ${
                        formErrors.WindSpeed ? "is-invalid" : ""
                      }`}
                      id="WindSpeed"
                      value={formData.WindSpeed}
                      onChange={handleChange}
                    />
                    <label htmlFor="WindSpeed">Wind Speed (mph)</label>
                    {formErrors.WindSpeed && (
                      <div className="invalid-feedback">{formErrors.WindSpeed}</div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <div className="form-floating">
                    <select
                      className={`form-control ${
                        formErrors.WindDirection ? "is-invalid" : ""
                      }`}
                      id="WindDirection"
                      value={formData.WindDirection}
                      onChange={handleChange}
                    >
                      <option value="">Select Wind Direction</option>
                      <option value="North">North</option>
                      <option value="Northeast">Northeast</option>
                      <option value="East">East</option>
                      <option value="Southeast">Southeast</option>
                      <option value="South">South</option>
                      <option value="Southwest">Southwest</option>
                      <option value="West">West</option>
                      <option value="Northwest">Northwest</option>
                    </select>
                    <label htmlFor="WindDirection">Wind Direction</label>
                    {formErrors.WindDirection && (
                      <div className="invalid-feedback">
                        {formErrors.WindDirection}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <div className="form-floating">
                    <select
                      className={`form-control ${
                        formErrors.MoonPhase ? "is-invalid" : ""
                      }`}
                      id="MoonPhase"
                      value={formData.MoonPhase}
                      onChange={handleChange}
                    >
                      <option value="">Select Moon Phase</option>
                      <option value="New">New</option>
                      <option value="First Qtr">First Qtr</option>
                      <option value="Full">Full</option>
                      <option value="Last Qtr">Last Qtr</option>
                    </select>
                    <label htmlFor="MoonPhase">Moon Phase</label>
                    {formErrors.MoonPhase && (
                      <div className="invalid-feedback">{formErrors.MoonPhase}</div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <div className="form-floating">
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control ${
                        formErrors.BaroMetricPressure ? "is-invalid" : ""
                      }`}
                      id="BaroMetricPressure"
                      value={formData.BaroMetricPressure}
                      onChange={handleChange}
                    />
                    <label htmlFor="BaroMetricPressure">
                      Barometric Pressure (mmHg)
                    </label>
                    {formErrors.BaroMetricPressure && (
                      <div className="invalid-feedback">
                        {formErrors.BaroMetricPressure}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="donezo-orders-panel donezo-upload-section mb-3">
            <div className="donezo-upload-section__header">
              <i className="fa fa-balance-scale" aria-hidden />
              <h2 className="donezo-upload-section__title">
                Weight &amp; length units
              </h2>
            </div>
            <div className="donezo-upload-section__body">
              <PondDetailStandardWeightUnits
                weightUnitId={selectedWeightUnit}
                lengthUnitId={selectedLengthUnit}
                onUnitsChange={handleStandardWeightUnitsChange}
                systemError={formErrors.StandardWeightSystem}
              />
            </div>
          </div>

          <div className="donezo-orders-panel donezo-upload-section mb-0">
            <div className="donezo-upload-section__header">
              <i className="fa fa-tint" aria-hidden />
              <h2 className="donezo-upload-section__title">Water Quality</h2>
            </div>
            <div className="donezo-upload-section__body">
              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <div className="form-floating">
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control ${
                        formErrors.WaterTemperature ? "is-invalid" : ""
                      }`}
                      id="WaterTemperature"
                      value={formData.WaterTemperature}
                      onChange={handleChange}
                    />
                    <label htmlFor="WaterTemperature">Water Temperature, F</label>
                    {formErrors.WaterTemperature && (
                      <div className="invalid-feedback">
                        {formErrors.WaterTemperature}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="form-floating">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control"
                      id="WaterClarity"
                      value={formData.WaterClarity}
                      onChange={handleChange}
                    />
                    <label htmlFor="WaterClarity">Water Clarity, ft.</label>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="form-floating">
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control ${formErrors.PH ? "is-invalid" : ""}`}
                      id="PH"
                      value={formData.PH}
                      onChange={handleChange}
                    />
                    <label htmlFor="PH">pH (1-14)</label>
                    {formErrors.PH && (
                      <div className="invalid-feedback">{formErrors.PH}</div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="form-floating">
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      id="DO"
                      value={formData.DO}
                      onChange={handleChange}
                    />
                    <label htmlFor="DO">Dissolved Oxygen, mg/L</label>
                  </div>
                </div>
                {WQ_EXTRA_FIELD_DEFS.map((def) => (
                  <div key={def.id} className="col-12 col-md-4">
                    <div className="form-floating">
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        id={def.id}
                        value={formData[def.id]}
                        onChange={handleChange}
                      />
                      <label htmlFor={def.id}>{def.label}</label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreatePondDetail;
